import React, { useContext, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  Paper,
  Tabs,
  Tab,
  Typography,
  Fade,
  styled,
} from "@mui/material";
import {
  Send as SendIcon,
  Comment as CommentIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { CMSContext } from "../context/CaseManagmentContext";
import { useSelector } from "react-redux";
import moment from "moment";

// Styled components
const ChatBubble = styled(Box, { shouldForwardProp: (prop) => prop !== 'isUser' })(({ isUser }) => ({
  maxWidth: "85%",
  padding: "8px 12px",
  borderRadius: "12px",
  marginBottom: "8px",
  backgroundColor: isUser ? "#e3f2fd" : "#f5f5f5",
  alignSelf: isUser ? "flex-end" : "flex-start",
  animation: "fadeIn 0.3s ease-in",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  "@keyframes fadeIn": {
    from: { opacity: 0, transform: "translateY(10px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
}));

export default function RightSideBar() {
  const { setData, selectedDocument, handleAddComment } =
    useContext(CMSContext);
  const userId = useSelector((state) => state.credit.userId);
  const [comment, setComment] = useState("");

  const handleSubmitComment = () => {
    if (comment.trim()) {
      handleAddComment(comment);
      setComment("");
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{ height: "100%", width: { xs: "298px", lg: "100%" } }}
    >
      {selectedDocument && (
        <>
          <div style={{ position: "relative" }}>
            <Tabs
              value={0}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                padding: 0,
                "& .MuiTabs-flexContainer": {
                  margin: 0,
                  padding: "5px",
                },
                "& .MuiTab-root": {
                  padding: "0px",
                  minHeight: "36px",
                },
              }}
            >
              <Tab
                icon={<CommentIcon sx={{ color: "#ff9800" }} />}
                label={<span style={{ color: "#003D78" }}>Comments</span>}
                sx={{
                  minWidth: 60,
                  fontSize: "10px",
                  "&.Mui-selected": {
                    color: "#003D78",
                  },
                  "& .MuiTab-wrapper": {
                    color: "#003D78",
                  },
                }}
              />
            </Tabs>
            <IconButton
              size="small"
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                padding: "4px",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
              onClick={() => {
                setData(null);
              }}
            >
              <CloseIcon sx={{ fontSize: 16, color: "#003D78" }} />
            </IconButton>
          </div>
          <Box
            sx={{
              height: "calc(100% - 60px)",
              display: "flex",
              flexDirection: "column",
              bgcolor: "white",
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                p: 2,
                bgcolor: "white",
                "::-webkit-scrollbar": {
                  width: "5px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#fff",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#bbb",
                  borderRadius: "6px",
                },
              }}
            >
              {selectedDocument.comments?.length > 0 ? (
                selectedDocument.comments.map((c, index) => (
                  <Fade in={true} timeout={300} key={index}>
                    <ChatBubble
                      sx={{ width: "100%" }}
                      isUser={c.commentedBy === userId}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={400}
                          color={
                            c.commentedBy === userId
                              ? "primary"
                              : "textSecondary"
                          }
                          sx={{
                            // maxWidth: "calc(100% - 20px)",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {c.commentedByName || "Unknown User"}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {c.commentText}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ fontSize: 10, alignSelf: "flex-end" }}
                      >
                        {moment(c.createdAt).format("DD-MM-YY h:mm A")}
                      </Typography>
                    </ChatBubble>
                  </Fade>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No comments yet
                </Typography>
              )}
            </Box>
            <Box sx={{ p: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Add a comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyPress={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    handleSubmitComment();
                  }
                }}
                size="small"
                InputLabelProps={{
                  sx: {
                    fontSize: "14px",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      color="primary"
                      onClick={() => {
                        handleSubmitComment();
                        setComment("");
                      }}
                      disabled={!comment.trim()}
                    >
                      <SendIcon />
                    </IconButton>
                  ),
                  sx: {
                    bgcolor: "white",
                    fontSize: "14px",
                    "& input:-webkit-autofill": {
                      backgroundColor: "white !important",
                      WebkitBoxShadow: "0 0 0 30px white inset !important",
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
}
